import { can } from './ability'
import { RbacActions, RbacSubjects, type RbacAction } from './constants'
import { useMyAbilityQuery } from './useMyAbilityQuery'

/**
 * Whether the current user is cleared for US-restricted data
 * (`Manage` on `UsRestrictedData`, granted by the "US-Restricted Data Access"
 * role). Use it to disable or hide anything that would expose such data, e.g.
 * downloading a file with employee coverage details.
 *
 * Pass `action` to require another action instead, such as `Download` for a
 * file download, matching EPS `UsRestrictedGuard.forAction`. `Manage` is the
 * action wildcard, so it still satisfies any action.
 *
 * Fails closed: unlike `usePermission`, it ignores the
 * `admin.rbac-enforcement-enabled` kill-switch, and denies while the ability
 * is loading, when it fails to load, and without the permission. The EPS
 * `UsRestrictedGuard` follows its own switch, so this check must never open
 * up on its own. UI only — the guard is the real boundary.
 *
 * @example
 *   const { hasUsRestrictedDataAccess, isLoading } = useUsRestrictedDataAccess()
 *   const { hasUsRestrictedDataAccess: canDownload } =
 *     useUsRestrictedDataAccess(RbacActions.Download)
 */
export function useUsRestrictedDataAccess(
  action: RbacAction = RbacActions.Manage
) {
  const { data, isLoading, isError } = useMyAbilityQuery()
  const hasUsRestrictedDataAccess =
    !isLoading &&
    !isError &&
    can(data?.rules, action, RbacSubjects.UsRestrictedData)
  return { hasUsRestrictedDataAccess, isLoading, isError }
}
